// Results tables of the Backtest page (src/pages/Backtest.js) -- everything
// rendered once a run completes: stat cards, equity curve, tier/open-
// holdings/trade-log tables, and the saved-run year/month breakdown tables.
// Pure code-motion (Tech Debt 2026-09 "Assessment and Backtest are god
// files" ticket) -- no behaviour change from the version formerly inline in
// Backtest.js. Purely prop-driven (data/inCad/cadPerUsd from the parent's
// run state), so this half of the split needed no fetch of its own -- the
// runner form half (SavedRunsPanel, ManifestAdvancedPanel, the run
// controls) stays in Backtest.js since it owns the actual `/api/backtest/run`
// fetch and run-management state.
import React, { useState, useMemo, useCallback } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  LogarithmicScale,
  PointElement,
  LineElement,
  Filler,
  Tooltip as ChartTooltip,
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import { CALL_BUCKET_MIN, PUT_SIGNAL_MAX } from '../../scoreThresholds';
import { fmtMoney, fmtPct } from './shared';
import { ddBasisLabel } from '../../utils/format';

ChartJS.register(CategoryScale, LinearScale, LogarithmicScale, PointElement, LineElement, Filler, ChartTooltip);

const CALL_BE = 56.3;
const PUT_BE  = 43.5;

const MONTH_NAMES = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

function fmtPnl(pnl, inCad, cadPerUsd) {
  if (pnl == null) return '—';
  const display = inCad ? pnl * cadPerUsd : pnl;
  const suf = inCad ? ' CAD' : ' USD';
  const sign = display >= 0 ? '+' : '-';
  const a = Math.abs(display);
  if (a >= 1e6)  return `${sign}$${(a / 1e6).toFixed(2)}M${suf}`;
  if (a >= 1e3)  return `${sign}$${(a / 1e3).toFixed(0)}k${suf}`;
  return `${sign}$${Math.round(a).toLocaleString()}${suf}`;
}

function StatCard({ label, value, sub, valueClass = 'text-gray-100' }) {
  return (
    <div className="rounded-md bg-trading-dark-900 border border-white/[0.06] px-3 py-2.5 min-w-0">
      <p className="text-[10px] font-medium tracking-wider uppercase text-gray-500 mb-1">{label}</p>
      <p className={`text-[15px] font-semibold font-mono leading-none ${valueClass}`}>{value}</p>
      {sub && <p className="text-[10px] text-gray-600 mt-1 leading-tight">{sub}</p>}
    </div>
  );
}
function EquityCurveChart({ equityCurve, inCad, cadPerUsd }) {
  const [logScale, setLogScale] = useState(false);

  const labels = useMemo(() => equityCurve.map(p => p.date), [equityCurve]);
  const values = useMemo(
    () => equityCurve.map(p => inCad ? p.equity * cadPerUsd : p.equity),
    [equityCurve, inCad, cadPerUsd],
  );

  const data = {
    labels,
    datasets: [{
      data:            values,
      borderColor:     '#8FB286',
      borderWidth:     1.5,
      pointRadius:     0,
      pointHoverRadius: 3,
      fill:            true,
      backgroundColor: (ctx) => {
        const chart = ctx.chart;
        const { ctx: c, chartArea } = chart;
        if (!chartArea) return 'transparent';
        const grad = c.createLinearGradient(0, chartArea.top, 0, chartArea.bottom);
        grad.addColorStop(0, 'rgba(143,178,134,0.18)');
        grad.addColorStop(1, 'rgba(143,178,134,0.01)');
        return grad;
      },
      tension: 0.2,
    }],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    animation: false,
    interaction: { mode: 'index', intersect: false },
    plugins: {
      legend: { display: false },
      tooltip: {
        backgroundColor: '#111113',
        borderColor: 'rgba(255,255,255,0.08)',
        borderWidth: 1,
        titleColor: '#9CA3AF',
        bodyColor: '#E5E7EB',
        titleFont: { size: 10, family: 'JetBrains Mono, monospace' },
        bodyFont:  { size: 11, family: 'JetBrains Mono, monospace' },
        padding: 8,
        callbacks: {
          label: (ctx) => {
            const v = ctx.parsed.y;
            return ` ${fmtMoney(inCad ? v / cadPerUsd : v, inCad, cadPerUsd)}`;
          },
        },
      },
    },
    scales: {
      x: {
        ticks: {
          color: '#4B5563',
          font: { size: 9, family: 'JetBrains Mono, monospace' },
          maxTicksLimit: 8,
          maxRotation: 0,
        },
        grid:   { color: 'rgba(255,255,255,0.04)', drawBorder: false },
        border: { display: false },
      },
      y: {
        type: logScale ? 'logarithmic' : 'linear',
        ticks: {
          color: '#4B5563',
          font: { size: 9, family: 'JetBrains Mono, monospace' },
          maxTicksLimit: 5,
          callback: (v) => fmtMoney(inCad ? v / cadPerUsd : v, inCad, cadPerUsd),
        },
        grid:   { color: 'rgba(255,255,255,0.04)', drawBorder: false },
        border: { display: false },
      },
    },
  };

  return (
    <div>
      <div className="flex items-center justify-end mb-1.5">
        <button
          onClick={() => setLogScale(s => !s)}
          className={`text-[9px] font-mono px-2 py-0.5 rounded border transition-colors ${
            logScale
              ? 'border-trading-green-400/40 text-trading-green-300 bg-trading-green-400/10'
              : 'border-white/[0.08] text-gray-600 hover:text-gray-400'
          }`}
        >
          {logScale ? 'LOG' : 'LIN'}
        </button>
      </div>
      <div style={{ height: 220 }}>
        <Line data={data} options={options} />
      </div>
    </div>
  );
}
function TierTable({ tierStats }) {
  return (
    <div className="rounded-md bg-trading-dark-900 border border-white/[0.06] overflow-hidden">
      <div className="px-3 py-2 border-b border-white/[0.06]">
        <p className="text-[11px] font-medium text-gray-400 uppercase tracking-wider">Tier Breakdown</p>
      </div>
      <table className="w-full">
        <thead>
          <tr className="border-b border-white/[0.04]">
            {['Tier', 'Alloc', 'N', 'TP%', 'Avg hold'].map(h => (
              <th key={h} className="px-3 py-1.5 text-left text-[9px] font-medium uppercase tracking-wider text-gray-600">{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {tierStats.filter(r => r.n > 0).map(row => (
            <tr key={row.tier} className="border-b border-white/[0.03] hover:bg-white/[0.02]">
              <td className="px-3 py-1.5 text-[11px] font-mono text-gray-300">{row.tier}</td>
              <td className="px-3 py-1.5 text-[11px] font-mono text-gray-500">{row.alloc_pct}%</td>
              <td className="px-3 py-1.5 text-[11px] font-mono text-gray-400">{row.n.toLocaleString()}</td>
              <td className={`px-3 py-1.5 text-[11px] font-mono ${
                row.tp_rate == null ? 'text-gray-600'
                  : row.tp_rate >= CALL_BE ? 'text-trading-green-400'
                  : 'text-trading-red-400'
              }`}>
                {row.tp_rate != null ? `${row.tp_rate}%` : '—'}
              </td>
              <td className="px-3 py-1.5 text-[11px] font-mono text-gray-500">
                {row.avg_hold != null ? `${row.avg_hold}d` : '—'}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
// ── Open Holdings ─────────────────────────────────────────────────────────────

// Column alignment contract (both tables):
//   T_o = T_c = 14.6 → every column with the same flex lands at the same pixel X.
//   Shared widths: SYM=2.0, Score/Orig=1.0, P&L/Curr=1.4, Alloc=1.4, Days=1.0,
//                 EntryDate=1.6, Entry$=1.2.  Exit$(3.4)=Curr$+TP$+SL$ in Open.

const OPEN_COL_DEFS = [
  { key: 'sym',      label: 'Sym',         flex: '2.0 0 0%' },
  { key: 'oscore',   label: 'Orig Score',  flex: '1.0 0 0%' },
  { key: 'cscore',   label: 'Curr Score',  flex: '1.4 0 0%' },
  { key: 'alloc',    label: 'Alloc',       flex: '1.4 0 0%' },
  { key: 'days',     label: 'Days Held',   flex: '1.0 0 0%' },
  { key: 'entry',    label: 'Entry Date',  flex: '1.6 0 0%' },
  { key: 'eprice',   label: 'Entry $',     flex: '1.2 0 0%' },
  { key: 'cprice',   label: 'Current $',   flex: '1.2 0 0%' },
  { key: 'tpprice',  label: 'TP $',        flex: '1.1 0 0%' },
  { key: 'slprice',  label: 'SL $',        flex: '1.1 0 0%' },
  { key: 'deadline', label: 'Hard Sell',   flex: '1.6 0 0%' },
];

function OpenHoldings({ holdings, inCad, cadPerUsd }) {
  if (!holdings || holdings.length === 0) return null;
  return (
    <div className="rounded-md bg-trading-dark-900 border border-amber-400/20 overflow-hidden">
      <div className="px-3 py-2 border-b border-amber-400/10">
        <p className="text-[11px] font-medium text-amber-400/80 uppercase tracking-wider">
          Open Holdings
          <span className="ml-2 text-gray-600 font-normal normal-case">
            ({holdings.length} position{holdings.length !== 1 ? 's' : ''} still open · marked at cost)
          </span>
        </p>
      </div>

      <div className="flex items-center border-b border-white/[0.04] px-2 py-1">
        {OPEN_COL_DEFS.map(col => (
          <div
            key={col.key}
            style={{ flex: col.flex }}
            className="px-1.5 text-[9px] font-medium uppercase tracking-wider text-gray-600 min-w-0"
          >
            {col.label}
          </div>
        ))}
      </div>

      <div className="overflow-y-auto" style={{ maxHeight: 300 }}>
        {holdings.map((h, i) => {
          const side     = h.side || 'call';
          const symColor = side === 'put' ? 'text-trading-red-400' : 'text-trading-green-400';
          const symLabel = `${side === 'put' ? 'P' : 'C'}:${h.symbol}`;
          const cur      = h.current_price;
          const curColor = !cur ? 'text-gray-600'
            : side === 'call'
              ? (cur >= h.entry_price ? 'text-trading-green-400' : 'text-trading-red-400')
              : (cur <= h.entry_price ? 'text-trading-green-400' : 'text-trading-red-400');
          const cs = h.current_score;
          const csColor = cs == null ? 'text-gray-600'
            : side === 'call'
              ? (cs >= CALL_BUCKET_MIN ? 'text-trading-green-400' : cs >= 50 ? 'text-gray-400' : 'text-trading-red-400/70')
              : (cs <= PUT_SIGNAL_MAX ? 'text-trading-red-400' : cs <= 50 ? 'text-gray-400' : 'text-trading-green-400/70');
          return (
            <div
              key={i}
              className="flex items-center border-b border-white/[0.03] px-2 py-0.5 hover:bg-white/[0.02]"
            >
              {/* C:SYM / P:SYM */}
              <div style={{ flex: OPEN_COL_DEFS[0].flex }} className={`px-1.5 text-[11px] font-mono font-medium min-w-0 truncate ${symColor}`}>
                {symLabel}
              </div>
              {/* Orig score */}
              <div style={{ flex: OPEN_COL_DEFS[1].flex }} className="px-1.5 text-[11px] font-mono text-gray-400 min-w-0">
                {h.score}
              </div>
              {/* Curr score — wide to match P&L cell in Closed Trades */}
              <div style={{ flex: OPEN_COL_DEFS[2].flex }} className={`px-1.5 text-[11px] font-mono font-medium min-w-0 ${csColor}`}>
                {cs != null ? cs : '—'}
              </div>
              {/* Alloc */}
              <div style={{ flex: OPEN_COL_DEFS[3].flex }} className="px-1.5 text-[10px] font-mono text-gray-500 min-w-0">
                {fmtMoney(h.premium, inCad, cadPerUsd)}
              </div>
              {/* Days held */}
              <div style={{ flex: OPEN_COL_DEFS[4].flex }} className="px-1.5 text-[10px] font-mono text-gray-600 min-w-0">
                {h.hold_bars}
              </div>
              {/* Entry date */}
              <div style={{ flex: OPEN_COL_DEFS[5].flex }} className="px-1.5 text-[10px] font-mono text-gray-600 min-w-0 truncate">
                {h.entry_date}
              </div>
              {/* Entry $ */}
              <div style={{ flex: OPEN_COL_DEFS[6].flex }} className="px-1.5 text-[10px] font-mono text-gray-500 min-w-0">
                {h.entry_price > 0 ? `$${h.entry_price.toFixed(2)}` : '—'}
              </div>
              {/* Current $ */}
              <div style={{ flex: OPEN_COL_DEFS[7].flex }} className={`px-1.5 text-[10px] font-mono font-medium min-w-0 ${curColor}`}>
                {cur ? `$${cur.toFixed(2)}` : '—'}
              </div>
              {/* TP $ */}
              <div style={{ flex: OPEN_COL_DEFS[8].flex }} className="px-1.5 text-[10px] font-mono text-trading-green-400/70 min-w-0">
                {h.tp_price > 0 ? `$${h.tp_price.toFixed(2)}` : '—'}
              </div>
              {/* SL $ */}
              <div style={{ flex: OPEN_COL_DEFS[9].flex }} className="px-1.5 text-[10px] font-mono text-trading-red-400/70 min-w-0">
                {h.sl_price > 0 ? `$${h.sl_price.toFixed(2)}` : '—'}
              </div>
              {/* Hard sell date */}
              <div style={{ flex: OPEN_COL_DEFS[10].flex }} className="px-1.5 text-[10px] font-mono text-amber-400/60 min-w-0 truncate">
                {h.hard_sell_date || '—'}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
// ── Trade Log ─────────────────────────────────────────────────────────────────
// Alignment contract: T_c = T_o = 14.6 (equal totals → pixel-perfect alignment).
// Open:   SYM(2.0)|Orig(1.0)|Curr(1.4)|Alloc(1.4)|Days(1.0)|ED(1.6)|E$(1.2)|Cur$(1.2)|TP(1.1)|SL(1.1)|Hard(1.6)
// Closed: SYM(2.0)|Scr (1.0)|P&L (1.4)|Alloc(1.4)|Days(1.0)|ED(1.6)|E$(1.2)|Exit$(3.4)         |ExitDate(1.6)
// Exit$(3.4) = Cur$(1.2)+TP(1.1)+SL(1.1), so Exit Date starts exactly where Hard Sell starts.

const COL_DEFS = [
  { key: 'sym',             label: 'Sym',          flex: '2.0 0 0%' },
  { key: 'score',           label: 'Score',        flex: '1.0 0 0%' },
  { key: 'pnl',             label: 'P&L',          flex: '1.32 0 0%' },
  { key: 'alloc',           label: 'Alloc',        flex: '1.4 0 0%' },
  { key: 'hold',            label: 'Days Held',    flex: '1.0 0 0%' },
  { key: 'entry',           label: 'Entry Date',   flex: '1.6 0 0%' },
  { key: 'entry_price',     label: 'Entry $',      flex: '1.2 0 0%' },
  { key: 'exit_price',      label: 'Exit $',       flex: '1.5 0 0%' },
  { key: 'portfolio_value', label: 'Portfolio $',  flex: '2.0 0 0%' },
  { key: 'exit',            label: 'Exit Date',    flex: '1.6 0 0%' },
];

function TradeLog({ trades, inCad, cadPerUsd }) {
  const [show, setShow] = useState(100);
  if (!trades || trades.length === 0) return null;

  const visible = trades.slice().reverse().slice(0, show);

  return (
    <div className="rounded-md bg-trading-dark-900 border border-white/[0.06] overflow-hidden">
      <div className="px-3 py-2 border-b border-white/[0.06] flex items-center justify-between">
        <p className="text-[11px] font-medium text-gray-400 uppercase tracking-wider">
          Closed Trades
          <span className="ml-2 text-gray-600 font-normal normal-case">({trades.length.toLocaleString()} · newest first)</span>
        </p>
        <div className="flex items-center gap-3">
          {[
            { label: 'TP',   color: 'bg-trading-green-400' },
            { label: 'SL',   color: 'bg-trading-red-400'   },
            { label: 'HARD', color: 'bg-amber-400'          },
          ].map(({ label, color }) => (
            <span key={label} className="flex items-center gap-1">
              <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${color}`} />
              <span className="text-[9px] font-mono text-gray-600">{label}</span>
            </span>
          ))}
        </div>
      </div>

      {/* Header row */}
      <div className="flex items-center border-b border-white/[0.04] px-2 py-1">
        {COL_DEFS.map(col => (
          <div
            key={col.key}
            style={{ flex: col.flex }}
            className="px-1.5 text-[9px] font-medium uppercase tracking-wider text-gray-600 min-w-0"
          >
            {col.label}
          </div>
        ))}
      </div>

      <div className="overflow-y-auto" style={{ maxHeight: 400 }}>
        {visible.map((t, i) => {
          const side     = t.side || 'call';
          const pnlColor = t.pnl >= 0 ? 'text-trading-green-400' : 'text-trading-red-400';
          const symColor = side === 'put' ? 'text-trading-red-400' : 'text-trading-green-400';
          const symLabel  = `${side === 'put' ? 'P' : 'C'}:${t.symbol}`;
          return (
            <div
              key={i}
              className="flex items-center border-b border-white/[0.03] px-2 py-0.5 hover:bg-white/[0.02]"
            >
              {/* C:SYM / P:SYM — colored by trade side */}
              <div style={{ flex: COL_DEFS[0].flex }} className={`px-1.5 text-[11px] font-mono font-medium min-w-0 truncate ${symColor}`}>
                {symLabel}
              </div>
              {/* Score */}
              <div style={{ flex: COL_DEFS[1].flex }} className="px-1.5 text-[11px] font-mono text-gray-400 min-w-0">
                {t.score}
              </div>
              {/* P&L — color encodes signed cash result */}
              <div
                style={{ flex: COL_DEFS[2].flex }}
                title={t.outcome ? `Outcome: ${String(t.outcome).toUpperCase()}` : undefined}
                className={`px-1.5 text-[10px] font-mono min-w-0 ${pnlColor}`}
              >
                {fmtPnl(t.pnl, inCad, cadPerUsd)}
              </div>
              {/* Alloc */}
              <div style={{ flex: COL_DEFS[3].flex }} className="px-1.5 text-[10px] font-mono text-gray-500 min-w-0">
                {fmtMoney(t.premium, inCad, cadPerUsd)}
              </div>
              {/* Days held */}
              <div style={{ flex: COL_DEFS[4].flex }} className="px-1.5 text-[10px] font-mono text-gray-600 min-w-0">
                {t.hold_bars}
              </div>
              {/* Entry date */}
              <div style={{ flex: COL_DEFS[5].flex }} className="px-1.5 text-[10px] font-mono text-gray-600 min-w-0 truncate">
                {t.entry_date}
              </div>
              {/* Entry $ */}
              <div style={{ flex: COL_DEFS[6].flex }} className="px-1.5 text-[10px] font-mono text-gray-500 min-w-0">
                {t.entry_price > 0 ? `$${t.entry_price.toFixed(2)}` : '—'}
              </div>
              {/* Exit $ */}
              <div style={{ flex: COL_DEFS[7].flex }} className="px-1.5 text-[10px] font-mono text-gray-500 min-w-0">
                {t.exit_price > 0 ? `$${t.exit_price.toFixed(2)}` : '—'}
              </div>
              {/* Portfolio $ — total portfolio value after this trade closes */}
              <div style={{ flex: COL_DEFS[8].flex }} className="px-1.5 text-[10px] font-mono text-sky-400/70 min-w-0 truncate">
                {t.portfolio_value ? fmtMoney(t.portfolio_value, inCad, cadPerUsd) : '—'}
              </div>
              {/* Exit date */}
              <div style={{ flex: COL_DEFS[9].flex }} className="px-1.5 text-[10px] font-mono text-gray-500 min-w-0 truncate">
                {t.exit_date}
              </div>
            </div>
          );
        })}
      </div>

      {trades.length > show && (
        <button
          onClick={() => setShow(s => s + 200)}
          className="w-full py-2 text-[11px] text-gray-500 hover:text-gray-300 hover:bg-white/[0.02] transition-colors border-t border-white/[0.04]"
        >
          Show more ({(trades.length - show).toLocaleString()} remaining)
        </button>
      )}
    </div>
  );
}
// ── Results ───────────────────────────────────────────────────────────────────

export default function Results({ data, inCad, cadPerUsd }) {
  const { summary: s, equity_curve, tier_stats, yearly, monthly, trade_log, open_holdings, params } = data;
  const [activeTab, setActiveTab] = useState('yearly');

  const fmt = useCallback((v) => fmtMoney(v, inCad, cadPerUsd), [inCad, cadPerUsd]);

  const returnClass = s.return_pct >= 0 ? 'text-trading-green-400' : 'text-trading-red-400';
  const ddClass     = s.max_dd <= 50 ? 'text-trading-green-400'
                    : s.max_dd <= 75 ? 'text-amber-400'
                    : 'text-trading-red-400';
  const tpClass     = s.tp_rate != null && s.tp_rate >= CALL_BE
                      ? 'text-trading-green-400' : 'text-trading-red-400';

  const currLabel = inCad ? 'CAD' : 'USD';
  // Drawdown basis: 'mtm' for runs from 2026-09-27 on; older saved runs are cost basis.
  const ddBasis = s.dd_basis || data.dd_basis || 'cost';

  return (
    <div className="space-y-3">
      {/* Context badges */}
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-[10px] font-mono text-gray-600 bg-white/[0.03] border border-white/[0.06] rounded px-2 py-0.5">
          {params.from} → {params.to}
        </span>
        <span className="text-[10px] font-mono text-gray-600 bg-white/[0.03] border border-white/[0.06] rounded px-2 py-0.5">
          {fmt(s.initial)} initial · {currLabel}
        </span>
        <span className="text-[10px] font-mono text-gray-600 bg-white/[0.03] border border-white/[0.06] rounded px-2 py-0.5">
          calls ≥ {params.min_score}
          {!params.calls_only && params.max_put_score != null && ` · puts ≤ ${params.max_put_score}`}
        </span>
        <span className="text-[10px] font-mono text-gray-600 bg-white/[0.03] border border-white/[0.06] rounded px-2 py-0.5">
          {params.portfolio_profile_name || params.portfolio_profile || 'Sentinel'} profile
        </span>
        {params.calls_only && (
          <span className="text-[10px] font-mono text-trading-green-400 bg-trading-green-900/20 border border-trading-green-400/20 rounded px-2 py-0.5">
            calls only
          </span>
        )}
        {!params.breadth_adaptive && (
          <span className="text-[10px] font-mono text-amber-400/70 bg-amber-400/5 border border-amber-400/15 rounded px-2 py-0.5">
            breadth adaptive OFF
          </span>
        )}
        {inCad && (
          <span className="text-[10px] font-mono text-amber-400/70 bg-amber-400/5 border border-amber-400/15 rounded px-2 py-0.5">
            1 USD ≈ {cadPerUsd.toFixed(4)} CAD
          </span>
        )}
        <span className="text-[10px] font-mono text-gray-700 ml-auto">v{params.version}</span>
      </div>

      {/* Summary stat cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2">
        <StatCard
          label={`Total Return (${currLabel})`}
          value={fmtPct(s.return_pct, 1)}
          sub={`${fmt(s.initial)} → ${fmt(s.final)}`}
          valueClass={returnClass}
        />
        <StatCard
          label="Max Drawdown"
          value={`${s.max_dd.toFixed(1)}%`}
          sub={ddBasis === 'mtm' && s.max_dd_cost != null
            ? `${ddBasisLabel(ddBasis)} · cost ${s.max_dd_cost.toFixed(1)}%`
            : ddBasisLabel(ddBasis)}
          valueClass={ddClass}
        />
        <StatCard
          label="TP Rate"
          value={s.tp_rate != null ? `${s.tp_rate}%` : '—'}
          sub={`BE ${s.be_calm}% calm · ${s.be_stressed}% stress`}
          valueClass={tpClass}
        />
        <StatCard
          label="Call TP"
          value={s.call_tp_rate != null ? `${s.call_tp_rate}%` : '—'}
          sub={`${s.n_calls.toLocaleString()} trades`}
          valueClass={s.call_tp_rate != null && s.call_tp_rate >= CALL_BE ? 'text-trading-green-400' : 'text-gray-300'}
        />
        {s.n_puts > 0 && (
          <StatCard
            label="Put TP"
            value={s.put_tp_rate != null ? `${s.put_tp_rate}%` : '—'}
            sub={`${s.n_puts.toLocaleString()} trades · BE ${s.be_put}%`}
            valueClass={s.put_tp_rate != null && s.put_tp_rate >= PUT_BE ? 'text-trading-green-400' : 'text-trading-red-400'}
          />
        )}
        <StatCard
          label="Closed Trades"
          value={s.n_trades.toLocaleString()}
          sub={`TP ${s.n_tp} · SL ${s.n_sl} · Hard ${s.n_hard}`}
        />
      </div>

      {/* Equity curve */}
      {equity_curve && equity_curve.length > 1 && (
        <div className="rounded-md bg-trading-dark-900 border border-white/[0.06] px-3 py-2.5">
          <p className="text-[10px] font-medium tracking-wider uppercase text-gray-600 mb-0">
            Portfolio Equity{inCad ? ' (CAD)' : ' (USD)'} · {ddBasisLabel(ddBasis)}
          </p>
          <EquityCurveChart equityCurve={equity_curve} inCad={inCad} cadPerUsd={cadPerUsd} />
        </div>
      )}

      {/* Tier + temporal tables */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        <TierTable tierStats={tier_stats} />

        {/* Tab switcher for year/month */}
        <div className="rounded-md bg-trading-dark-900 border border-white/[0.06] overflow-hidden">
          <div className="px-3 py-2 border-b border-white/[0.06] flex items-center gap-3">
            {['yearly', 'monthly'].map(tab => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`text-[10px] font-medium uppercase tracking-wider transition-colors ${
                  activeTab === tab ? 'text-trading-green-300' : 'text-gray-600 hover:text-gray-400'
                }`}
              >
                {tab === 'yearly' ? 'Year-by-Year' : 'Month-by-Month'}
              </button>
            ))}
          </div>
          {activeTab === 'yearly'
            ? <YearTableInner yearly={yearly} />
            : <MonthTableInner monthly={monthly} />
          }
        </div>
      </div>

      {/* Open holdings */}
      <OpenHoldings holdings={open_holdings} inCad={inCad} cadPerUsd={cadPerUsd} />

      {/* Trade log */}
      <TradeLog trades={trade_log} inCad={inCad} cadPerUsd={cadPerUsd} />
    </div>
  );
}
function YearTableInner({ yearly }) {
  if (!yearly || yearly.length === 0) return <p className="px-3 py-4 text-[11px] text-gray-600">No data</p>;
  return (
    <table className="w-full">
      <thead>
        <tr className="border-b border-white/[0.04]">
          {['Year', 'N', 'TP%', 'C TP%', 'P TP%', 'Return'].map(h => (
            <th key={h} className="px-3 py-1.5 text-left text-[9px] font-medium uppercase tracking-wider text-gray-600">{h}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {yearly.map(row => (
          <tr key={row.year} className="border-b border-white/[0.03] hover:bg-white/[0.02]">
            <td className="px-3 py-1.5 text-[11px] font-mono text-gray-300">{row.year}</td>
            <td className="px-3 py-1.5 text-[11px] font-mono text-gray-400">{(row.n_trades || 0).toLocaleString()}</td>
            <td className={`px-3 py-1.5 text-[11px] font-mono ${
              row.tp_rate == null ? 'text-gray-600' : row.tp_rate >= CALL_BE ? 'text-trading-green-400' : 'text-trading-red-400'
            }`}>{row.tp_rate != null ? `${row.tp_rate}%` : '—'}</td>
            <td className="px-3 py-1.5 text-[11px] font-mono text-gray-500">{row.call_tp_rate != null ? `${row.call_tp_rate}%` : '—'}</td>
            <td className="px-3 py-1.5 text-[11px] font-mono text-gray-500">{row.put_tp_rate != null ? `${row.put_tp_rate}%` : '—'}</td>
            <td className={`px-3 py-1.5 text-[11px] font-mono ${
              row.return_pct == null ? 'text-gray-600' : row.return_pct >= 0 ? 'text-trading-green-400' : 'text-trading-red-400'
            }`}>{row.return_pct != null ? fmtPct(row.return_pct, 1) : '—'}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
function MonthTableInner({ monthly }) {
  const enriched = useMemo(() => (monthly || []).map((row, idx) => {
    const prev = idx > 0 ? monthly[idx - 1] : null;
    let mom_pct = null;
    if (prev && prev.equity_end != null && row.equity_end != null && prev.equity_end > 0) {
      mom_pct = (row.equity_end / prev.equity_end - 1) * 100;
    }
    return { ...row, mom_pct };
  }), [monthly]);

  if (!enriched.length) return <p className="px-3 py-4 text-[11px] text-gray-600">No data</p>;

  return (
    <div className="overflow-y-auto" style={{ maxHeight: 320 }}>
      <table className="w-full">
        <thead className="sticky top-0 bg-trading-dark-900">
          <tr className="border-b border-white/[0.04]">
            {['Month', 'N', 'TP%', 'C TP%', 'P TP%', 'Return'].map(h => (
              <th key={h} className="px-3 py-1.5 text-left text-[9px] font-medium uppercase tracking-wider text-gray-600">{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {enriched.map((row, i) => (
            <tr key={i} className="border-b border-white/[0.03] hover:bg-white/[0.02]">
              <td className="px-3 py-1 text-[10px] font-mono text-gray-400">
                {row.year} <span className="text-gray-600">{MONTH_NAMES[row.month - 1]}</span>
              </td>
              <td className="px-3 py-1 text-[10px] font-mono text-gray-400">{row.n_trades}</td>
              <td className={`px-3 py-1 text-[10px] font-mono ${
                row.tp_rate == null ? 'text-gray-600' : row.tp_rate >= CALL_BE ? 'text-trading-green-400' : 'text-trading-red-400'
              }`}>{row.tp_rate != null ? `${row.tp_rate}%` : '—'}</td>
              <td className="px-3 py-1 text-[10px] font-mono text-gray-500">{row.call_tp_rate != null ? `${row.call_tp_rate}%` : '—'}</td>
              <td className="px-3 py-1 text-[10px] font-mono text-gray-500">{row.put_tp_rate != null ? `${row.put_tp_rate}%` : '—'}</td>
              <td className={`px-3 py-1 text-[10px] font-mono ${
                row.mom_pct == null ? 'text-gray-600' : row.mom_pct >= 0 ? 'text-trading-green-400' : 'text-trading-red-400'
              }`}>{row.mom_pct != null ? fmtPct(row.mom_pct, 1) : '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
